/**
 * VoicemailPanel - Voicemail management panel for TotalConnectRTC
 * 
 * Displays voicemail messages, allows playback, deletion, and manages settings
 * Integrates with TotalConnectRTC CallSettingsPanel style
 */

'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  Voicemail,
  Play,
  Pause,
  Trash2,
  Phone,
  Clock,
  Calendar,
  Download,
  Volume2,
  Loader2,
  Settings,
  Bell,
  MessageSquare,
  RefreshCw,
  Check
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { useAuth } from '@/hooks/useAuth';
import { notify, confirm } from '@/lib/notifications';
import { cn } from '@/lib/utils';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

interface VoicemailMessage {
  id: string;
  caller_number: string;
  caller_name?: string;
  recipient_extension?: string;
  extension_number?: string;
  duration_seconds?: number;
  duration?: number;
  created_at: string;
  is_read: boolean;
  audio_url?: string;
  recording_url?: string;
  transcription?: string;
}

interface VoicemailSettings {
  id?: string;
  extension_id: string;
  extension_number: string;
  greeting_type: 'default' | 'custom' | 'name';
  greeting_audio_url?: string;
  email_notification: boolean;
  notification_email?: string;
  transcription_enabled: boolean;
  max_message_length: number;
  pin?: string;
}

interface VoicemailExtensionOption {
  id: string;
  extension_number?: string;
  user_id?: number | string | null;
}

export interface VoicemailPanelProps {
  onClose: () => void;
  isDark?: boolean;
  primaryColor?: string;
  extensionId?: string;
  extensionNumber?: string;
}

export function VoicemailPanel({
  onClose,
  isDark = true,
  primaryColor = '#0ea5e9',
  extensionId: propExtensionId,
  extensionNumber: propExtensionNumber
}: VoicemailPanelProps) {
  const { user } = useAuth();
  const userId = user?.id;
  const [activeTab, setActiveTab] = useState<'messages' | 'settings'>('messages');
  const [voicemails, setVoicemails] = useState<VoicemailMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Extension state - fetched dynamically if not provided
  const [extensionId, setExtensionId] = useState<string | undefined>(propExtensionId);
  const [extensionNumber, setExtensionNumber] = useState<string | undefined>(propExtensionNumber);
  const [loadingExtension, setLoadingExtension] = useState(!propExtensionId);

  // Settings state
  const [settings, setSettings] = useState<VoicemailSettings | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState<Partial<VoicemailSettings>>({
    greeting_type: 'default',
    email_notification: false,
    transcription_enabled: false,
    max_message_length: 120
  });

  // Fetch user's SIP extension if not provided as prop
  const fetchUserExtension = useCallback(async () => {
    if (propExtensionId) {
      setExtensionId(propExtensionId);
      setExtensionNumber(propExtensionNumber);
      setLoadingExtension(false);
      return;
    }

    try {
      const tokens = authService.getTokens();
      const res = await fetch('/api/v1/sip', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'getExtensions' })
      });

      const data = await res.json();
      if (data.success && data.data?.length > 0) {
        // Find the current user's extension by matching user_id
        const extensions = data.data as VoicemailExtensionOption[];
        const userExtension = extensions.find(
          (ext) => ext.user_id && String(ext.user_id) === String(userId),
        ) || extensions[0]; // Fallback to first extension
        if (userExtension) {
          setExtensionId(userExtension.id);
          setExtensionNumber(userExtension.extension_number);
        } else {
          notify.error('No SIP extension found for your account');
        }
      } else {
        notify.error('No SIP extension configured');
      }
    } catch (e) {
      console.error('Error fetching extension:', e);
      notify.error('Failed to fetch extension information');
    } finally {
      setLoadingExtension(false);
    }
  }, [propExtensionId, propExtensionNumber, userId]);

  const fetchVoicemails = useCallback(async () => {
    try {
      const tokens = authService.getTokens();
      const res = await fetch('/api/v1/sip', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'getVoicemails' })
      });

      const data = await res.json();
      if (data.success) {
        setVoicemails(data.data || []);
      } else {
        notify.error(data.error || 'Failed to fetch voicemails');
      }
    } catch (e) {
      console.error('Error fetching voicemails:', e);
      notify.error('Failed to load voicemails');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const tokens = authService.getTokens();
      const res = await fetch('/api/v1/sip', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'getVoicemailSettings' })
      });

      const data = await res.json();
      if (data.success && data.data?.length > 0) {
        // If extension ID is provided, find settings for that extension
        const extSettings = extensionId 
          ? data.data.find((s: VoicemailSettings) => String(s.extension_id) === String(extensionId))
          : data.data[0]; // Otherwise use first available
        
        if (extSettings) {
          setSettings(extSettings);
          setSettingsForm(extSettings);
        }
      }
    } catch (e) {
      console.error('Error fetching settings:', e);
    }
  }, [extensionId]);

  useEffect(() => {
    void fetchUserExtension();
  }, [fetchUserExtension]);

  useEffect(() => {
    if (!loadingExtension && extensionId) {
      void fetchVoicemails();
      void fetchSettings();
    }
  }, [loadingExtension, extensionId, fetchVoicemails, fetchSettings]);

  const handleRefresh = () => {
    setRefreshing(true);
    if (!extensionId) {
      fetchUserExtension().then(() => {
        fetchVoicemails();
        fetchSettings();
      });
    } else {
      fetchVoicemails();
      fetchSettings();
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      const tokens = authService.getTokens();
      const res = await fetch('/api/v1/sip', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'markVoicemailRead', voicemail_id: id })
      });

      const data = await res.json();
      if (data.success) {
        setVoicemails(vms => vms.map(vm => 
          vm.id === id ? { ...vm, is_read: true } : vm
        ));
      }
    } catch (e) {
      console.error('Error marking as read:', e);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ message: 'Delete this voicemail?', variant: 'danger' }))) return;

    try {
      const tokens = authService.getTokens();
      const res = await fetch('/api/v1/sip', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'deleteVoicemail', voicemail_id: id })
      });

      const data = await res.json();
      if (data.success) {
        notify.success('Voicemail deleted');
        setVoicemails(vms => vms.filter(vm => vm.id !== id));
        if (playingId === id) {
          setPlayingId(null);
          if (audioRef.current) {
            audioRef.current.pause();
          }
        }
      } else {
        notify.error(data.error || 'Failed to delete');
      }
    } catch (e) {
      notify.error('Error deleting voicemail');
    }
  };

  const handlePlay = (vm: VoicemailMessage) => {
    const url = vm.audio_url || vm.recording_url;
    if (!url) return;

    if (playingId === vm.id) {
      // Pause if already playing
      setPlayingId(null);
      if (audioRef.current) {
        audioRef.current.pause();
      }
    } else {
      // Play new
      setPlayingId(vm.id);
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.play();
      } else {
        audioRef.current = new Audio(url);
        audioRef.current.play();
      }

      // Mark as read when playing
      if (!vm.is_read) {
        handleMarkAsRead(vm.id);
      }

      // Reset when finished
      audioRef.current.onended = () => {
        setPlayingId(null);
      };
    }
  };

  const handleSaveSettings = async () => {
    if (!extensionId) {
      notify.error('No extension selected');
      return;
    }

    setSavingSettings(true);
    try {
      const tokens = authService.getTokens();
      const res = await fetch('/api/v1/sip', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: settings?.id ? 'updateVoicemailSettings' : 'createVoicemailSettings',
          ...(settings?.id ? { id: settings.id } : {}),
          extension_id: extensionId,
          extension_number: extensionNumber || '',
          ...settingsForm
        })
      });

      const data = await res.json();
      if (data.success) {
        notify.success('Settings saved');
        fetchSettings();
      } else {
        notify.error(data.error || 'Failed to save settings');
      }
    } catch (e) {
      notify.error('Error saving settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const unheardCount = voicemails.filter(vm => !vm.is_read).length;
  const totalDuration = voicemails.reduce((sum, vm) => sum + (vm.duration_seconds || vm.duration || 0), 0);

  return (
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-20 p-4">
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl w-full max-w-2xl overflow-hidden max-h-[85vh] flex flex-col`}>
        {/* Header */}
        <div className={`px-4 py-3 border-b flex items-center justify-between flex-shrink-0 ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: `${primaryColor}20` }}>
              <Voicemail className="w-5 h-5" style={{ color: primaryColor }} />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Voicemail
              </h3>
              {extensionNumber && (
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Extension {extensionNumber}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="Refresh"
            >
              <RefreshCw className={cn(
                `w-5 h-5`,
                refreshing && 'animate-spin',
                isDark ? 'text-gray-400' : 'text-gray-500'
              )} />
            </button>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
            >
              <X className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className={`px-4 py-3 border-b grid grid-cols-3 gap-4 ${
          isDark ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'
        }`}>
          <div>
            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Messages</div>
            <div className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {voicemails.length}
            </div>
          </div>
          <div>
            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Unheard</div>
            <div className="text-xl font-bold text-red-500">
              {unheardCount}
            </div>
          </div>
          <div>
            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Duration</div>
            <div className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {Math.floor(totalDuration / 60)}m {totalDuration % 60}s
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className={`flex gap-2 px-4 py-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setActiveTab('messages')}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
              activeTab === 'messages'
                ? `${isDark ? 'bg-gray-700' : 'bg-gray-100'} ${isDark ? 'text-white' : 'text-gray-900'}`
                : `${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-900'}`
            )}
          >
            <MessageSquare className="w-4 h-4" />
            Messages
            {voicemails.length > 0 && (
              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                {voicemails.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
              activeTab === 'settings'
                ? `${isDark ? 'bg-gray-700' : 'bg-gray-100'} ${isDark ? 'text-white' : 'text-gray-900'}`
                : `${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-900'}`
            )}
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {(loading || loadingExtension) ? (
            <div className="flex flex-col items-center justify-center py-12">
              <BrandedLoadingSpinner mode="compact" size="sm" primaryColor={primaryColor} showSurface={false} showProgressBar={false} />
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {loadingExtension ? 'Loading extension...' : 'Loading voicemails...'}
              </p>
            </div>
          ) : !extensionId ? (
            <div className={`text-center py-12 rounded-xl border-2 border-dashed ${isDark ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 bg-red-500/20">
                <Bell className="w-8 h-8 text-red-500" />
              </div>
              <p className={`font-medium text-red-600 dark:text-red-400 mb-2`}>
                No SIP Extension Configured
              </p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Please configure a SIP extension in PBX Settings to use voicemail
              </p>
            </div>
          ) : activeTab === 'messages' ? (
            voicemails.length === 0 ? (
              <div className={`text-center py-12 rounded-xl ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${primaryColor}20` }}>
                  <Voicemail className="w-8 h-8" style={{ color: primaryColor }} />
                </div>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  No voicemail messages
                </p>
                <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Incoming voicemails will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {voicemails.map(vm => (
                  <div
                    key={vm.id}
                    className={cn(
                      'rounded-xl p-4 border-2 transition-all',
                      !vm.is_read
                        ? `border-blue-500/30 ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`
                        : `${isDark ? 'border-gray-700 bg-gray-700/30' : 'border-gray-200 bg-white'}`
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn(
                        'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
                        !vm.is_read
                          ? 'bg-blue-500'
                          : isDark ? 'bg-gray-600' : 'bg-gray-200'
                      )}>
                        <Phone className={cn('w-5 h-5', !vm.is_read ? 'text-white' : 'text-gray-400')} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {vm.caller_name || vm.caller_number || 'Unknown'}
                          </span>
                          {!vm.is_read && (
                            <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">New</span>
                          )}
                        </div>
                        <div className={`flex items-center gap-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            Ext {vm.recipient_extension || vm.extension_number || '?'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {vm.duration_seconds || vm.duration || 0}s
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(vm.created_at).toLocaleString()}
                          </span>
                        </div>
                        {vm.transcription && (
                          <div className={`mt-2 p-2 rounded-lg text-sm italic ${
                            isDark ? 'bg-gray-800/50 text-gray-300' : 'bg-gray-50 text-gray-600'
                          }`}>
                            "{vm.transcription}"
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {(vm.audio_url || vm.recording_url) && (
                          <button
                            onClick={() => handlePlay(vm)}
                            className={`p-2 rounded-lg transition-colors ${
                              playingId === vm.id
                                ? 'bg-blue-500 text-white'
                                : isDark ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'
                            }`}
                            title={playingId === vm.id ? 'Pause' : 'Play'}
                          >
                            {playingId === vm.id ? (
                              <Pause className="w-4 h-4" />
                            ) : (
                              <Play className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(vm.id)}
                          className={`p-2 rounded-lg transition-colors ${
                            isDark ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400' : 'bg-red-50 hover:bg-red-100 text-red-600'
                          }`}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            /* Settings Tab */
            <div className="space-y-4">
              <div className={`rounded-xl p-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Greeting Type
                </label>
                <select
                  value={settingsForm.greeting_type}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, greeting_type: e.target.value as 'default' | 'custom' | 'name' }))}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDark
                      ? 'bg-gray-600 border-gray-500 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="default">Default Greeting</option>
                  <option value="name">Name Only (e.g., "Extension 100 is unavailable")</option>
                  <option value="custom">Custom Greeting</option>
                </select>
              </div>

              <div className={`rounded-xl p-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settingsForm.email_notification || false}
                    onChange={(e) => setSettingsForm(prev => ({ ...prev, email_notification: e.target.checked }))}
                    className="w-4 h-4 rounded"
                    style={{ accentColor: primaryColor }}
                  />
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Email Notification
                  </span>
                </label>
                {settingsForm.email_notification && (
                  <input
                    type="email"
                    value={settingsForm.notification_email || ''}
                    onChange={(e) => setSettingsForm(prev => ({ ...prev, notification_email: e.target.value }))}
                    placeholder="your-email@example.com"
                    className={`mt-2 w-full px-3 py-2 rounded-lg border ${
                      isDark
                        ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                  />
                )}
              </div>

              <div className={`rounded-xl p-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settingsForm.transcription_enabled || false}
                    onChange={(e) => setSettingsForm(prev => ({ ...prev, transcription_enabled: e.target.checked }))}
                    className="w-4 h-4 rounded"
                    style={{ accentColor: primaryColor }}
                  />
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Enable Transcription
                  </span>
                </label>
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Automatically transcribe voicemail messages to text
                </p>
              </div>

              <div className={`rounded-xl p-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Max Message Length (seconds)
                </label>
                <input
                  type="number"
                  value={settingsForm.max_message_length || 120}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, max_message_length: parseInt(e.target.value) || 120 }))}
                  min="30"
                  max="300"
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDark
                      ? 'bg-gray-600 border-gray-500 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              <div className={`rounded-xl p-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Voicemail PIN
                </label>
                <input
                  type="password"
                  value={settingsForm.pin || ''}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, pin: e.target.value }))}
                  placeholder="4-6 digit PIN"
                  maxLength={6}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDark
                      ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  PIN required to access voicemail remotely
                </p>
              </div>

              <button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className={`w-full py-3 rounded-xl font-medium text-white transition-all ${
                  savingSettings ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                }`}
                style={{ backgroundColor: primaryColor }}
              >
                {savingSettings ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Check className="w-5 h-5" />
                    Save Settings
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
